/**
 * Admin panel chat hook for CometChat headless SDK.
 *
 * Joins the order's CometChat group, fetches message history,
 * and listens for real-time messages. Provides send functions
 * for text messages.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { useCometChat } from '../providers/CometChatProvider';
import Logger from '../utils/Logger';

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  photoUrl?: string;
  timestamp: number;
  isOwn: boolean;
  type: 'text' | 'image' | 'system';
}

const MESSAGES_LIMIT = 30;

export function useOrderChat(orderId: string | null) {
  const { isLoggedIn } = useCometChat();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const listenerIdRef = useRef('');
  const guid = orderId ? `order_${orderId}` : null;

  const getLoggedInUserId = useCallback((): string | null => {
    try {
      const authStr = localStorage.getItem('auth');
      if (!authStr) return null;
      return JSON.parse(authStr)?.id || null;
    } catch {
      return null;
    }
  }, []);

  const mapMessage = useCallback(
    (msg: CometChat.BaseMessage): ChatMessage | null => {
      const sender = msg.getSender();
      if (!sender) return null;
      const uid = getLoggedInUserId();
      const type = msg.getType();
      const textMsg = msg as CometChat.TextMessage;
      const mediaMsg = msg as CometChat.MediaMessage;

      return {
        id: String(msg.getId()),
        senderId: sender.getUid(),
        senderName: sender.getName(),
        text: type === 'text' ? textMsg.getText() : '',
        photoUrl: type === 'image' ? mediaMsg.getURL() : undefined,
        timestamp: msg.getSentAt(),
        isOwn: sender.getUid() === uid,
        type: type === 'image' ? 'image' : 'text',
      };
    },
    [getLoggedInUserId],
  );

  useEffect(() => {
    if (!isLoggedIn || !guid) {
      setMessages([]);
      return;
    }

    let cancelled = false;
    const listenerId = `admin_chat_${guid}_${Date.now()}`;
    listenerIdRef.current = listenerId;

    // Must join group before fetching messages or receiving events.
    // If already a member, CometChat returns the group (not an error).
    const joinAndInit = async () => {
      try {
        await CometChat.joinGroup(guid, CometChat.GroupType.Private, '');
      } catch (err: any) {
        // ERR_ALREADY_JOINED is expected if we re-open the chat
        if (err?.code !== 'ERR_ALREADY_JOINED') {
          Logger.error('[useOrderChat] joinGroup error:', err);
          return;
        }
      }

      if (cancelled) return;

      try {
        const request = new CometChat.MessagesRequestBuilder()
          .setGUID(guid)
          .setLimit(MESSAGES_LIMIT)
          .build();
        const fetched = await request.fetchPrevious();
        if (!cancelled) {
          const mapped = fetched.map(mapMessage).filter(Boolean) as ChatMessage[];
          setMessages(mapped);
        }
      } catch (err) {
        Logger.error('[useOrderChat] fetch error:', err);
      }
    };

    joinAndInit();

    // Real-time listener
    CometChat.addMessageListener(
      listenerId,
      new CometChat.MessageListener({
        onTextMessageReceived: (msg: CometChat.TextMessage) => {
          const mapped = mapMessage(msg);
          if (mapped) {
            setMessages((prev) => [...prev, mapped]);
            if (!mapped.isOwn) setUnreadCount((c) => c + 1);
          }
        },
        onMediaMessageReceived: (msg: CometChat.MediaMessage) => {
          const mapped = mapMessage(msg);
          if (mapped) {
            setMessages((prev) => [...prev, mapped]);
            if (!mapped.isOwn) setUnreadCount((c) => c + 1);
          }
        },
        onTypingStarted: () => setIsTyping(true),
        onTypingEnded: () => setIsTyping(false),
      }),
    );

    return () => {
      cancelled = true;
      CometChat.removeMessageListener(listenerId);
    };
  }, [isLoggedIn, guid, mapMessage]);

  const sendText = useCallback(
    async (text: string) => {
      if (!guid || !text.trim()) return;
      const msg = new CometChat.TextMessage(guid, text.trim(), CometChat.RECEIVER_TYPE.GROUP);
      const sent = await CometChat.sendMessage(msg);
      const mapped = mapMessage(sent);
      if (mapped) setMessages((prev) => [...prev, mapped]);
    },
    [guid, mapMessage],
  );

  const clearUnread = useCallback(() => setUnreadCount(0), []);

  return { messages, sendText, isTyping, unreadCount, clearUnread, isReady: isLoggedIn };
}
